import * as React from "react";
import * as RechartsPrimitive from "recharts";

// Format: { THEME_NAME: CSS_SELECTOR }
export const THEMES = { light: "", dark: ".dark" } as const;

// Tipos base para dados de gráficos
type ChartDataItem = {
  [key: string]: number | string | Date;
};

// Configuração de cores e temas (compatível com shadcn)
export type ChartColorConfig =
  | { color?: string; theme?: never }
  | { color?: never; theme: Record<keyof typeof THEMES, string> };

// Configuração de série de dados (compatível com shadcn)
export type ChartSeriesConfig = {
  label?: React.ReactNode;
  icon?: React.ComponentType;
  yAxisId?: string;
} & ChartColorConfig;

// Configuração geral do gráfico (compatível com shadcn)
export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    yAxisId?: string;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  );
};

// Props do contexto do gráfico
export type ChartContextProps = {
  config: ChartConfig;
};

// Props do contêiner do gráfico
export type ChartContainerProps = React.ComponentProps<"div"> & {
  config: ChartConfig;
  children: React.ComponentProps<
    typeof RechartsPrimitive.ResponsiveContainer
  >["children"];
};

// Props do estilo do gráfico
export type ChartStyleProps = {
  id: string;
  config: ChartConfig;
};

// Props do conteúdo do tooltip
export type ChartTooltipContentProps = React.ComponentProps<
  typeof RechartsPrimitive.Tooltip
> &
  React.ComponentProps<"div"> & {
    hideLabel?: boolean;
    hideIndicator?: boolean;
    indicator?: "line" | "dot" | "dashed";
    nameKey?: string;
    labelKey?: string;
  };

// Props do conteúdo da legenda
export type ChartLegendContentProps = React.ComponentProps<"div"> &
  Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign"> & {
    hideIcon?: boolean;
    nameKey?: string;
  };

// Props base para todos os gráficos
export type BaseChartProps = {
  data: ChartDataItem[];
  config: ChartConfig;
  className?: string;
  height?: number | string;
  width?: number | string;
  showTooltip?: boolean;
  showLegend?: boolean;
  showGrid?: boolean;
  loading?: boolean;
  error?: string | null;
};

// Props específicas para gráficos com eixos
export type AxisChartProps = BaseChartProps & {
  xKey: string;
  yKeys: string[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  xAxisFormatter?: (value: any) => string;
  yAxisFormatter?: (value: any) => string;
  showXAxis?: boolean;
  showYAxis?: boolean;
};

// Props para gráficos de pizza/radial
export type PieChartProps = BaseChartProps & {
  dataKey: string;
  nameKey: string;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
};

// Status do gráfico
export type ChartStatus = "idle" | "loading" | "success" | "error";

// Variações de gráficos
export type ChartVariant =
  | "default"
  | "linear"
  | "step"
  | "stacked"
  | "stacked-expanded"
  | "legend"
  | "gradient"
  | "horizontal"
  | "vertical"
  | "traditional"
  | "multiple"
  | "mixed"
  | "positive"
  | "negative";

// Props específicas para gráficos de área
export type AreaChartProps = AxisChartProps & {
  variant?: ChartVariant;
  fillOpacity?: number;
  strokeWidth?: number;
  smooth?: boolean;
  stacked?: boolean;
  showGradient?: boolean;
  showLegend?: boolean;
};

// Props específicas para gráficos de barra
export type BarChartProps = AxisChartProps & {
  variant?: ChartVariant;
  barSize?: number;
  barGap?: number;
  stacked?: boolean;
  horizontal?: boolean;
  showLegend?: boolean;
  showLabels?: boolean; // Para mostrar labels nas barras
  customLabels?: boolean; // Para labels customizados
  mixed?: boolean; // Para barras mistas (positivas/negativas)
  positiveColor?: string; // Cor para valores positivos
  negativeColor?: string; // Cor para valores negativos
};
